import { defineStyleConfig } from '@chakra-ui/react';

const TEXT_PRIMARY = 'text.primary';

/**
 * The Button component style.
 *
 * Split out of `components.ts` in 2026-09, on the seam the file's own review
 * named: Button was 162 of its 996 lines against a hard 1000-line CI ceiling
 * (`scripts/check-file-sizes.sh`), so the next variant anyone added reddened
 * the build. Same pattern as `components.modal.ts` and `components.kbd.ts` —
 * one component per module, re-exported from `components.ts`, which stays the
 * single map handed to `extendTheme`.
 *
 * WHY `link` AND `solid` ARE DEFINED HERE. They are Chakra's, not ours — and
 * that was the bug. `extendTheme` MERGES, so a variant this file does not
 * declare falls through to Chakra's base button, which is keyed on
 * `colorScheme` — and `Button.defaultProps` sets `colorScheme: 'gray'`. So 19
 * `variant="link"` call sites were rendering Chakra's stock `gray.500`
 * (#718096), measured at 3.75:1 on `bg.canvas` — under WCAG 1.4.3's 4.5:1 for
 * body text — in a cool blue-grey that is off-palette for a warm parchment
 * app. `solid` had the same shape with a `gray.100` fill.
 *
 * The contrast suite could not see it: `__tests__/support/resolveToken.ts`
 * resolves `theme.semanticTokens.colors`, and these colours never passed
 * through a semantic token. Declaring them here puts them back inside the
 * system that is actually measured.
 */
const Button = defineStyleConfig({
  baseStyle: {
    fontWeight: 'semibold',
    borderRadius: 'md',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    // `_focusVisible`, NOT `_focus`. With `_focus` every button kept a purple
    // ring after being CLICKED and held it until something else took focus —
    // so a selected filter tab, a pinned sidebar row and its pin button all sat
    // there permanently outlined. The global `*:focus { outline: none }` in
    // styles.ts only suppressed the native outline, not this boxShadow.
    // Keyboard users still get the ring; that is the whole distinction
    // `:focus-visible` exists to draw.
    _focusVisible: {
      boxShadow: 'outline',
    },
    // Single consolidated icon override — forces all icons to inherit button text color.
    // Overrides MaterialIcon's default color (text.secondary + !important in sx).
    '& .chakra-icon, & svg, & [class*="material-symbols"]': {
      color: 'currentColor !important',
    },
  },
  sizes: {
    sm: {
      fontSize: 'sm',
      px: 3,
      py: 2,
    },
    md: {
      fontSize: 'md',
      px: 4,
      py: 2.5,
    },
    lg: {
      fontSize: 'lg',
      px: 6,
      py: 3,
    },
  },
  variants: {
    // Primary CTA — twilight blue (`brand.primary`) with white text.
    // Hover/active step down through the twilight ramp via semantic
    // brand.hover / brand.active tokens (NOT raw palette keys, which would
    // shift to grayscale).
    primary: {
      bg: 'brand.primary',
      color: 'text.onBrand',
      _hover: {
        bg: 'brand.hover',
        _disabled: { bg: 'brand.primary' },
      },
      _active: {
        bg: 'brand.active',
      },
    },
    // Neutral outline — hairline grey border + primary text. The default
    // "secondary" CTA in modern tech-app patterns (Linear / Vercel).
    outline: {
      bg: 'transparent',
      color: 'text.primary',
      border: '1px solid',
      borderColor: 'border.default',
      _hover: {
        bg: 'bg.subtle',
        borderColor: 'border.hover',
      },
      _active: {
        bg: 'bg.muted',
      },
    },
    // Option card — full-width tappable tile used in multi-step modals (e.g.
    // Add equipment's entry options). Outline at rest; brand-tinted on hover.
    // Auto-height so multi-line descriptions expand naturally.
    'outline-option': {
      bg: 'transparent',
      color: 'text.primary',
      border: '1px solid',
      borderColor: 'border.default',
      h: 'auto',
      p: 4,
      justifyContent: 'flex-start',
      _hover: {
        bg: 'brand.surface',
        borderColor: 'brand.primary',
      },
      _active: {
        bg: 'bg.muted',
      },
    },
    // Primary outline — twilight border + twilight text. A prominent
    // secondary CTA that reads as "the main action" without the visual weight
    // of a filled `primary`. Use when an outline button still needs to draw the
    // eye (e.g. the Connect CTA on a connector card, where a neutral grey
    // `outline` disappears). Pairs with a solid `primary` for the dominant
    // action so the hierarchy is solid-primary > outline-primary > grey
    // `outline`. Brand tokens only — never raw palette keys.
    'outline-primary': {
      bg: 'transparent',
      color: 'brand.onSurface',
      border: '1px solid',
      borderColor: 'brand.primary',
      _hover: {
        bg: 'brand.surface',
        borderColor: 'brand.hover',
        color: 'brand.hover',
        _disabled: { bg: 'transparent', color: 'brand.onSurface' },
      },
      _active: {
        bg: 'brand.muted',
      },
    },
    // Secondary button — quiet pill on the warm neutral ramp
    // (`brand.secondary` = `neutral.200`, NOT Chakra's cool `gray.100`).
    // Uses text.primary (near-black ink), not text.onBrand (white) which
    // would render invisibly on the grey background.
    secondary: {
      bg: 'brand.secondary',
      color: 'text.primary',
      _hover: {
        bg: 'brand.secondary.hover',
      },
      _active: {
        bg: 'brand.secondary.active',
      },
    },
    // Ghost button for sidebar items
    ghost: {
      color: TEXT_PRIMARY,
      _hover: {
        bg: 'bg.hover',
      },
      _active: {
        bg: 'bg.active',
      },
    },

    /**
     * DESTRUCTIVE ACTIONS — revoke, disconnect, delete, forget.
     *
     * WHY THESE EXIST. Twenty-one buttons across the app said
     * `colorScheme="red"`, and not one of them rendered red. `colorScheme`
     * feeds only Chakra's own colorScheme machinery, and a variant THIS file
     * declares wins the `extendTheme` merge before that machinery runs.
     * Measured on the live theme: `outline({ colorScheme: 'red' })` resolves
     * `borderColor: 'border.default'`, `ghost` resolves `color: 'text.primary'`.
     * So "Revoke all", "Disconnect" and the annotation bin were styled exactly
     * like the Cancel button beside them.
     *
     * The ten sites that passed NO variant were worse: they fell to
     * `defaultProps.variant = 'primary'`, so "Delete Forever" and "Delete
     * workspace & continue" rendered in the BRAND BLUE, the same fill as Save.
     *
     * Three variants because the call sites already used three shapes — 10 with
     * no variant (a dialog's confirm), 8 `outline`, 3 `ghost`. Named
     * `<shape>-<intent>` to match the existing `outline-primary` /
     * `outline-option`.
     *
     * Like every variant here they are colorScheme-INERT. `danger` IS the
     * intent; there is nothing left for a `colorScheme` to say, and
     * `colorScheme="red"` on a Button is now an ESLint error for that reason.
     */

    /**
     * The destructive CTA — the confirm button in a delete dialog.
     *
     * Shaped on `primary`, because that is structurally what it is: the action
     * the dialog exists to take.
     *
     * Three distinct steps, per the ladder rule in `semanticTokens.ts`: white
     * clears 1.4.3 on all of them (5.57 / 7.71 / 10.83), so the press is
     * visible without the label ever dropping below AA. See that file for why
     * the fill takes no `_dark` pair — it is a measured palette limit, not an
     * analogy to `brand.primary`.
     */
    danger: {
      bg: 'interactive.danger',
      color: 'text.onBrand',
      _hover: {
        bg: 'interactive.danger.hover',
        _disabled: { bg: 'interactive.danger' },
      },
      _active: { bg: 'interactive.danger.active' },
    },

    /**
     * The bordered form — the most common shape at the call sites, and the
     * right default for a destructive action that should read as available
     * rather than as the loudest thing on the page. The label carries the
     * warning.
     *
     * CONTRAST. `text.error` is 7.71:1 on the panel and 7.20:1 on a parchment
     * box in light, 5.89:1 in dark, and it stays past 4.5:1 on both tints it is
     * drawn over. The border deliberately matches the neutral `outline` it
     * replaces (1.43:1 against the panel in light, vs 1.38:1): this theme has
     * always bounded outline buttons with a hairline, and making only the
     * danger one heavier would read as a different control.
     */
    'outline-danger': {
      bg: 'transparent',
      color: 'text.error',
      border: '1px solid',
      borderColor: 'border.error',
      _hover: {
        bg: 'bg.error',
        borderColor: 'text.error',
      },
      _active: {
        bg: 'bg.error.active',
        borderColor: 'text.error',
      },
    },

    /**
     * The same intent with no chrome — for a destructive action inside a row or
     * a toolbar, where a border would draw a box around one item in a set that
     * has none. Mirrors `ghost`'s three-step shape on the danger tints.
     */
    'ghost-danger': {
      color: 'text.error',
      _hover: {
        bg: 'bg.error',
      },
      _active: {
        bg: 'bg.error.active',
      },
    },

    /**
     * POSITIVE and CAUTIONING actions, the other two ends of the same axis.
     *
     * WHY THEY EXIST is the danger story again, one step further out: when the
     * red buttons were fixed it was recorded that 22 more sites passed a
     * `colorScheme` that could not reach the screen either. `colorScheme` is
     * inert on EVERY variant this file declares, for EVERY value — measured,
     * not assumed — so "Move to Trash" (`colorScheme="orange"`, no variant)
     * rendered in the BRAND BLUE, and the copy-confirmation and PHI-enable
     * buttons that asked for green rendered neutral.
     *
     * TWO SHAPES EACH, NOT THREE. `danger` has a ghost form because four call
     * sites needed one; success and warning have none, so none is declared.
     * Three lines each if that changes.
     *
     * The tokens carry the reasoning and the measurements — in particular why
     * warning's ladder runs 400 -> 500 -> 600 while every other filled variant
     * runs dark-ward. See `semanticTokens.ts`.
     */

    /**
     * A positive confirming action — "Enable", and the copied state of a
     * copy-to-clipboard button.
     *
     * White clears 1.4.3 on all three steps (4.98 / 7.26 / 10.42).
     */
    success: {
      bg: 'interactive.success',
      color: 'text.onBrand',
      _hover: {
        bg: 'interactive.success.hover',
        _disabled: { bg: 'interactive.success' },
      },
      _active: { bg: 'interactive.success.active' },
    },

    /**
     * The bordered form — a success state that should not shout, and the shape
     * both existing call sites already use. `text.success` is 7.26:1 on the
     * panel and 6.78:1 on a parchment box in light, 9.86:1 in dark, and stays
     * past 4.5:1 on both tints it is drawn over.
     */
    'outline-success': {
      bg: 'transparent',
      color: 'text.success',
      border: '1px solid',
      borderColor: 'border.success',
      _hover: {
        bg: 'bg.success',
        borderColor: 'text.success',
      },
      _active: {
        bg: 'bg.success.active',
        borderColor: 'text.success',
      },
    },

    /**
     * A cautioning action — "Move to Trash". Reversible, which is what keeps it
     * out of `danger`, and consequential, which is what keeps it off `primary`.
     *
     * The ONLY filled variant here that does not take white. `text.onWarning`
     * is a fixed near-black, because white cannot clear AA anywhere on this
     * ramp while it still looks amber. 9.61 / 8.34 / 5.88 across the states.
     */
    warning: {
      bg: 'interactive.warning',
      color: 'text.onWarning',
      _hover: {
        bg: 'interactive.warning.hover',
        _disabled: { bg: 'interactive.warning' },
      },
      _active: { bg: 'interactive.warning.active' },
    },

    /**
     * The bordered form — used by `StatusBanner`'s action in its warning tone.
     * `text.warning` is 6.31:1 on the panel and 5.89:1 on a parchment box in
     * light, 10.31:1 in dark.
     */
    'outline-warning': {
      bg: 'transparent',
      color: 'text.warning',
      border: '1px solid',
      borderColor: 'border.warning',
      _hover: {
        bg: 'bg.warning',
        borderColor: 'text.warning',
      },
      _active: {
        bg: 'bg.warning.active',
        borderColor: 'text.warning',
      },
    },

    // Chakra's own variants, redeclared so they resolve through OUR tokens.
    // See the header: undeclared variants fall through to Chakra's base button
    // keyed on `colorScheme: 'gray'`, which shipped `gray.500` link text at
    // 3.75:1 and a cool `gray.100` solid fill in a warm parchment app.
    //
    // NOTE `link` and `solid` above are colorScheme-INERT, like every other
    // variant in this file: the declared object wins the `extendTheme` merge,
    // so `colorScheme="red"` on one now does nothing. `outline` always behaved
    // this way. Callers that want danger state it (`color="text.error"`); the
    // two that passed `colorScheme="red"` were updated with it. A button
    // that needs the destructive treatment takes a `danger` variant above.
    link: {
      padding: 0,
      height: 'auto',
      lineHeight: 'normal',
      verticalAlign: 'baseline',
      // 6.18:1 on the canvas, 6.62:1 on the panel — the same token every
      // in-prose anchor uses, so a button that looks like a link matches one.
      color: 'text.link',
      _hover: { textDecoration: 'underline', _disabled: { textDecoration: 'none' } },
      _active: { color: 'brand.active' },
    },
    // The neutral filled button. `primary` remains the brand CTA; this is the
    // quiet one, and it now sits on the warm neutral ramp rather than Chakra's
    // blue-tinted greys.
    solid: {
      bg: 'bg.subtle',
      color: TEXT_PRIMARY,
      _hover: { bg: 'bg.hover', _disabled: { bg: 'bg.subtle' } },
      _active: { bg: 'bg.active' },
    },
    // Top-bar action in <DetailTopBar>, across notes / collections /
    // attachments. A hairline PILL since the 2026-09 pass, not the ghost it
    // was: a row of borderless buttons above a document reads as the
    // document's own chrome, while outlined pills read as controls acting on
    // it — and it matches the note view's Tags / Links / Attachments pills one
    // row below. Distinct from inline-content buttons (`outline`) and the
    // primary CTA (`primary`). Pair with `leftIcon=<MaterialIcon …/>` and a
    // responsive label: `<Box display={{ base: 'none', md: 'inline' }}>`.
    topbar: {
      bg: 'frame.panel',
      color: 'text.secondary',
      border: '1px solid',
      borderColor: 'border.default',
      borderRadius: 'full',
      fontWeight: 'normal',
      fontSize: '13px',
      px: 3,
      h: '32px',
      minH: '32px',
      _hover: { bg: 'bg.hover', color: 'text.primary' },
      // `bg.active`, not `bg.muted`. This button rests on `frame.panel`, and
      // in LIGHT the muted tint inverts the ladder outright: hover
      // neutral.300 then press neutral.200, a step back TOWARD the page.
      //
      // Dark has been wrong in two different ways and is now merely weak.
      // `bg.muted` was #1C211C, identical to the page before the 2026-09 panel
      // lift (invisible) and below it after (inverted). The 2026-09-22 tint
      // separation took it to #252B25, so it is finally above the page — but
      // only at 1.077:1, against `bg.hover`'s 1.201 and `bg.active`'s 1.466. A
      // press that lands between the page and its own hover is not a press.
      // `bg.active` is the step above `bg.hover` in both modes, which is what
      // a press means here.
      _active: { bg: 'bg.active' },
      _disabled: { opacity: 0.5, cursor: 'not-allowed', _hover: { bg: 'frame.panel' } },
    },
  },
  defaultProps: {
    size: 'md',
    variant: 'primary',
  },
});

export { Button };
